import { useEffect, useState } from "react";
import { api, ApiError, TimeEntry, TimeEntryPage } from "../lib/api";

function formatMinutes(m: number): string {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthAgoISO(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
}

export default function MyTimeEntries() {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [dateFrom, setDateFrom] = useState(monthAgoISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [offset, setOffset] = useState(0);
  const pageSize = 25;

  useEffect(() => {
    void loadEntries();
  }, [dateFrom, dateTo, offset]);

  async function loadEntries() {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      params.set("limit", String(pageSize));
      params.set("offset", String(offset));
      const page = await api.get<TimeEntryPage>(`/time-entries?${params}`);
      setEntries(page.items);
      setTotal(page.total);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load entries");
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.ceil(total / pageSize);
  const currentPage = Math.floor(offset / pageSize) + 1;

  return (
    <div className="admin">
      <header className="admin__head">
        <div>
          <div className="page-eyebrow">Time & mileage</div>
          <h1>My time entries</h1>
        </div>
      </header>

      {err && <div className="alert alert--error">{err}</div>}

      <div className="te-filters">
        <label className="te-filters__field">
          <span>From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }}
          />
        </label>
        <label className="te-filters__field">
          <span>To</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setOffset(0); }}
          />
        </label>
      </div>

      <div className="admin__scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>Miles</th>
              <th>Note</th>
              <th>Case</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="muted">Loading…</td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={5} className="muted">No time entries found.</td></tr>
            )}
            {entries.map((e) => (
              <tr key={e.id}>
                <td>{e.entry_date}</td>
                <td>{formatMinutes(e.minutes)}</td>
                <td>{e.mileage ?? "—"}</td>
                <td className="te-recent__note">{e.note || "—"}</td>
                <td>{e.case_name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="te-pagination">
          <button
            type="button"
            className="btn btn--sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - pageSize))}
          >
            ← Prev
          </button>
          <span className="muted">
            Page {currentPage} of {totalPages} ({total} entries)
          </span>
          <button
            type="button"
            className="btn btn--sm"
            disabled={currentPage >= totalPages}
            onClick={() => setOffset(offset + pageSize)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
